import React, { useState } from 'react';
import { Hammer, Lock, Instagram, Smartphone, Sparkles, Trophy } from 'lucide-react';

interface UnderConstructionProps {
  logoUrl?: string;
  primaryColor?: string;
  instagram?: string;
  phone?: string;
  onAdminLoginClick: () => void;
}

export const UnderConstruction: React.FC<UnderConstructionProps> = ({
  logoUrl,
  primaryColor = '#eab308',
  instagram,
  phone,
  onAdminLoginClick
}) => {
  const [hoveredIcon, setHoveredIcon] = useState(false);

  return (
    <div className="min-h-screen w-full bg-[#020617] text-white flex flex-col items-center justify-center p-4 relative overflow-hidden select-none">
      {/* Dynamic Glowing background circles */}
      <div 
        className="absolute w-[400px] h-[400px] rounded-full filter blur-[120px] opacity-10 animate-pulse transition-all duration-[8000ms]"
        style={{ 
          backgroundColor: primaryColor,
          top: '15%',
          left: '10%'
        }}
      />
      <div 
        className="absolute w-[500px] h-[500px] rounded-full filter blur-[150px] opacity-10 animate-pulse transition-all duration-[10000ms]"
        style={{ 
          backgroundColor: primaryColor,
          bottom: '10%',
          right: '5%'
        }}
      />

      {/* Decorative top grid */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.02),transparent_60%)] pointer-events-none" />

      {/* Main Container with Glassmorphism */}
      <div className="relative z-10 max-w-xl w-full text-center px-6 py-12 md:py-16 rounded-3xl bg-[#090d16]/60 border border-white/[0.06] backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col items-center transition-all duration-500 hover:border-white/[0.1] hover:shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
        
        {/* Sparkles / Floating elements */}
        <div className="absolute -top-3 -left-3 animate-bounce duration-[3000ms]">
          <Sparkles className="opacity-40" size={24} style={{ color: primaryColor }} />
        </div>
        <div className="absolute -bottom-4 -right-2 animate-bounce duration-[4000ms]">
          <Trophy className="opacity-30" size={26} style={{ color: primaryColor }} />
        </div>

        {/* Pulsing Brand Logo / App Icon */}
        <div 
          className="mb-8 cursor-pointer relative group"
          onClick={onAdminLoginClick}
          title="Doble clic para acceder al panel"
        >
          {logoUrl ? (
            <div className="relative">
              <div 
                className="absolute inset-0 rounded-full blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-500"
                style={{ backgroundColor: primaryColor }}
              />
              <img 
                src={logoUrl} 
                alt="App Logo" 
                className="w-24 h-24 md:w-28 md:h-28 object-contain rounded-2xl relative z-10 transform transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          ) : (
            <div 
              className="w-24 h-24 md:w-28 md:h-28 rounded-2xl flex items-center justify-center relative z-10 shadow-2xl transition-all duration-500 group-hover:rotate-6"
              style={{ backgroundColor: `${primaryColor}20`, border: `2px solid ${primaryColor}40` }}
            >
              <Hammer size={44} style={{ color: primaryColor }} className="animate-wiggle" />
            </div>
          )}
          
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-black/80 border border-white/10 rounded-full px-2.5 py-0.5 text-[8px] font-black tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap">
            ADMIN BYPASS
          </div>
        </div>

        {/* Construction Icon Header */}
        <div 
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.03] border border-white/[0.05] mb-6 transition-all"
          onMouseEnter={() => setHoveredIcon(true)}
          onMouseLeave={() => setHoveredIcon(false)}
        >
          <Hammer 
            size={16} 
            style={{ color: primaryColor }} 
            className={`transition-transform duration-500 ${hoveredIcon ? 'rotate-[15deg] scale-110' : 'animate-pulse'}`}
          />
          <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
            Próximamente disponible
          </span>
        </div>

        {/* Beautiful Title with Neon Glow */}
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-4 leading-tight">
          Sitio en{' '}
          <span 
            className="bg-clip-text text-transparent bg-gradient-to-r font-black"
            style={{ 
              backgroundImage: `linear-gradient(to right, #ffffff, ${primaryColor}, #ffffff)`,
              textShadow: `0 0 30px ${primaryColor}20`
            }}
          >
            Construcción
          </span>
        </h1>

        {/* Friendly Message */}
        <p className="text-zinc-400 text-sm md:text-base leading-relaxed mb-8 max-w-md">
          Estamos afinando los últimos detalles para brindarte la plataforma definitiva del padel local: gestión de torneos, red social, estadisticas, historiales, reservas de canchas, ranking oficial y más. Ya falta poco!
        </p>

        {/* Social / Contact Links */}
        {(instagram || phone) && (
          <div className="w-full flex flex-col gap-3 items-center justify-center pt-4 border-t border-white/[0.04]">
            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">¿Querés consultarnos algo?</p>
            <div className="flex gap-4 items-center">
              {instagram && (
                <a 
                  href={`https://instagram.com/${instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-xs font-semibold text-zinc-300 hover:text-white bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] rounded-xl px-4 py-2 transition-all duration-300"
                >
                  <Instagram size={14} className="text-pink-500" />
                  <span>@{instagram.replace('@', '')}</span>
                </a>
              )}
              {phone && (
                <div className="flex items-center gap-2 text-xs font-semibold text-zinc-300 bg-white/[0.02] border border-white/[0.04] rounded-xl px-4 py-2">
                  <Smartphone size={14} style={{ color: primaryColor }} />
                  <span>{phone}</span>
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Footer / Admin Access Trigger */}
      <div className="mt-12 text-center relative z-10 flex flex-col items-center gap-2">
        <button 
          onClick={onAdminLoginClick}
          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-white bg-transparent border border-zinc-800 hover:border-zinc-600 rounded-full px-5 py-2 transition-all duration-300"
        >
          <Lock size={12} style={{ color: primaryColor }} />
          <span>Acceso Privado / Sede</span>
        </button>
        <p className="text-[9px] text-zinc-600 font-medium">Todos los derechos reservados © {new Date().getFullYear()}</p>
      </div>
    </div>
  );
};
